import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ORDER_APIS } from "@/api_framework/api_config";
import { IPackingAlertEvent, IPackingAlertsCountResponse } from "@/api_framework/api_modals/Orders";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useIsClinicOpsUser } from "@/app/hooks/useIsClinicOpsUser";
import { PACKING_ALERTS_REFRESH_EVENT } from "@/constants";

interface UsePackingAlertsOptions {
  /** Extra gate on top of the role/clinic checks (e.g. false in mirror view). */
  enabled?: boolean;
}

/** How long the "order received" announcement stays on the navbar pill. */
export const PACKING_ANNOUNCEMENT_MS = 10_000;
/** Reconnect delays; the last value is reused for every further attempt. */
const RECONNECT_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];
/** Server sends a keep-alive comment every 15s; 45s of silence = dead stream. */
const STREAM_IDLE_TIMEOUT_MS = 45_000;
/** At most one token-refresh attempt per this window after the stream gets a 401. */
const AUTH_RECOVERY_INTERVAL_MS = 60_000;

type PackingAlertsChannelMessage =
  | { kind: "event"; event: IPackingAlertEvent }
  | { kind: "sync"; event: IPackingAlertEvent }
  | { kind: "sync-request" };

const PACKING_EVENT_TYPES: ReadonlyArray<IPackingAlertEvent["type"]> = ["snapshot", "order_received", "count_updated"];

/** Parses one SSE frame ("data: {...}" lines). Returns null for comments / junk. */
const parseSSEFrame = (frame: string): IPackingAlertEvent | null => {
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (!line || line.startsWith(":")) continue; // keep-alive comment
    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).replace(/^ /, ""));
    }
  }
  if (dataLines.length === 0) return null;
  try {
    const parsed = JSON.parse(dataLines.join("\n")) as IPackingAlertEvent;
    if (!parsed || !PACKING_EVENT_TYPES.includes(parsed.type) || typeof parsed.pending_count !== "number") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Live "orders pending packing" count for clinic_ops users.
 *
 * - Streams GET /vms/packing-alerts/stream/ (SSE over fetch, since it needs the
 *   Authorization header) for the currently mapped clinic.
 * - Reconnects with backoff on stream end / error / 45s silence; stops on 401/403.
 * - Re-fetches the count API when the tab becomes visible again or the OMS
 *   Confirmed tab is opened (PACKING_ALERTS_REFRESH_EVENT). No interval polling.
 * - `announcementCount` > 0 while the 10s "new order" announcement is active;
 *   orders arriving inside the window are grouped and the timer restarts.
 *
 * - One stream per browser per clinic: tabs elect a leader via navigator.locks;
 *   followers receive the leader's events over BroadcastChannel (falls back to
 *   one stream per tab where either API is missing).
 *
 * Use it once per tab (it is mounted in the app Header).
 */
export const usePackingAlerts = (options?: UsePackingAlertsOptions) => {
  const configuration = useConfiguration();
  const isClinicOpsUser = useIsClinicOpsUser();
  const clinicId = useAppSelector((state) => state.root.UIStates.mappedClinicId);
  const accessToken = useAppSelector((state) => state?.auth?.authorization?.accessToken);

  const isEnabled = (options?.enabled ?? true) && isClinicOpsUser && Boolean(clinicId);

  const [pendingCount, setPendingCount] = useState(0);
  const [announcementCount, setAnnouncementCount] = useState(0);
  // Bumped to re-open the stream after a 401 once a fresh token is available.
  const [retryNonce, setRetryNonce] = useState(0);

  const lastAppliedTsRef = useRef(0);
  const pendingCountRef = useRef(0);
  const clinicIdRef = useRef(clinicId);
  const announcementTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stoppedUnauthorizedRef = useRef(false);
  const lastAuthRecoveryAtRef = useRef(0);
  const countControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    clinicIdRef.current = clinicId;
  }, [clinicId]);

  const getAuthorization = useCallback(
    () => localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key) ?? "",
    [configuration],
  );

  const clearAnnouncement = useCallback(() => {
    if (announcementTimerRef.current) {
      clearTimeout(announcementTimerRef.current);
      announcementTimerRef.current = null;
    }
    setAnnouncementCount(0);
  }, []);

  /** Applies an event to this tab's state. Returns false when it was ignored. */
  const applyEvent = useCallback((event: IPackingAlertEvent): boolean => {
    if (event.clinic_id && event.clinic_id !== clinicIdRef.current) return false;
    // Out-of-order protection: ignore anything older than what we already applied.
    if (typeof event.ts === "number" && event.ts < lastAppliedTsRef.current) return false;
    if (typeof event.ts === "number") lastAppliedTsRef.current = event.ts;

    pendingCountRef.current = Math.max(0, event.pending_count);
    setPendingCount(pendingCountRef.current);

    if (event.type === "order_received") {
      setAnnouncementCount((prev) => prev + 1);
      if (announcementTimerRef.current) clearTimeout(announcementTimerRef.current);
      announcementTimerRef.current = setTimeout(() => {
        announcementTimerRef.current = null;
        setAnnouncementCount(0);
      }, PACKING_ANNOUNCEMENT_MS);
    }
    return true;
  }, []);

  /** Safety-net refresh via the count API (visibility / Confirmed tab). */
  const refreshCount = useCallback(async () => {
    const activeClinicId = clinicIdRef.current;
    if (!activeClinicId) return;

    countControllerRef.current?.abort();
    const controller = new AbortController();
    countControllerRef.current = controller;
    // If a stream event lands while this request is in flight, it is newer — keep it.
    const tsAtRequest = lastAppliedTsRef.current;

    try {
      const response = await axios.get<IPackingAlertsCountResponse>(ORDER_APIS?.PACKING_ALERTS_COUNT?.baseURL ?? "", {
        params: { clinic_id: activeClinicId },
        headers: { Authorization: getAuthorization() },
        signal: controller.signal,
      });
      if (
        response.data?.status &&
        typeof response.data?.data?.pending_count === "number" &&
        clinicIdRef.current === activeClinicId &&
        lastAppliedTsRef.current === tsAtRequest
      ) {
        pendingCountRef.current = Math.max(0, response.data.data.pending_count);
        setPendingCount(pendingCountRef.current);
      }
    } catch (error: any) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") return;
      // Best-effort safety net; the stream remains the source of truth.
    } finally {
      if (countControllerRef.current === controller) countControllerRef.current = null;
    }
  }, [getAuthorization]);

  // SSE stream lifecycle. One stream per browser per clinic: tabs elect a leader
  // with the Web Locks API; only the leader opens the fetch stream and it
  // rebroadcasts every applied event over a BroadcastChannel to follower tabs.
  // Without locks/BroadcastChannel every tab runs its own stream (old behaviour).
  // Everything is torn down via `session` on clinic change / logout / unmount / HMR.
  useEffect(() => {
    if (!isEnabled) return;

    const session = new AbortController();
    const streamUrl = `${ORDER_APIS?.PACKING_ALERTS_STREAM?.baseURL ?? ""}?clinic_id=${encodeURIComponent(clinicId)}`;
    const coordinationKey = `packing-alerts:${clinicId}`;
    const canCoordinate =
      typeof navigator !== "undefined" && Boolean(navigator.locks) && typeof BroadcastChannel !== "undefined";
    let channel: BroadcastChannel | null = null;
    let isLeader = false;

    stoppedUnauthorizedRef.current = false;
    lastAppliedTsRef.current = 0;
    pendingCountRef.current = 0;
    setPendingCount(0);

    const postToChannel = (message: PackingAlertsChannelMessage) => {
      try {
        channel?.postMessage(message);
      } catch {
        // Channel already closed (teardown race) — nothing to do.
      }
    };

    /** Leader / single-tab path: apply locally, then fan out to follower tabs. */
    const handleStreamEvent = (event: IPackingAlertEvent) => {
      if (applyEvent(event)) postToChannel({ kind: "event", event });
    };

    /**
     * Runs the fetch stream with reconnect/backoff. Resolves only when the session
     * is aborted; after a 401/403 it stays pending (holding leadership) so other
     * tabs don't retry the same refused request.
     */
    const runStream = () =>
      new Promise<void>((resolve) => {
        let attempt = 0;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

        const finish = () => {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          resolve();
        };
        if (session.signal.aborted) {
          finish();
          return;
        }
        session.signal.addEventListener("abort", finish, { once: true });

        const connect = async () => {
          if (session.signal.aborted) return;

          const connection = new AbortController();
          const abortConnection = () => connection.abort();
          session.signal.addEventListener("abort", abortConnection);

          let idleTimer: ReturnType<typeof setTimeout> | null = null;
          const armIdleWatchdog = () => {
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(abortConnection, STREAM_IDLE_TIMEOUT_MS);
          };

          let shouldReconnect = true;
          try {
            armIdleWatchdog();
            const response = await fetch(streamUrl, {
              method: "GET",
              headers: { Authorization: getAuthorization(), Accept: "text/event-stream" },
              cache: "no-store",
              signal: connection.signal,
            });

            if (response.status === 401) {
              // Token expired: stop, and let one axios call refresh it (the app's
              // interceptor refreshes on 401 and updates the store); the effect
              // below reconnects when the new token lands. Throttled so a token the
              // server keeps rejecting can't turn into a refresh loop.
              stoppedUnauthorizedRef.current = true;
              shouldReconnect = false;
              if (Date.now() - lastAuthRecoveryAtRef.current > AUTH_RECOVERY_INTERVAL_MS) {
                lastAuthRecoveryAtRef.current = Date.now();
                void refreshCount();
              }
            } else if (response.status === 403) {
              // Not a clinic_ops user on the backend — stop silently.
              shouldReconnect = false;
            } else if (response.ok && response.body) {
              const reader = response.body.getReader();
              const decoder = new TextDecoder();
              let buffer = "";

              // eslint-disable-next-line no-constant-condition
              while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                armIdleWatchdog();
                attempt = 0; // bytes are flowing — connection is healthy

                buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
                let boundary = buffer.indexOf("\n\n");
                while (boundary !== -1) {
                  const frame = buffer.slice(0, boundary);
                  buffer = buffer.slice(boundary + 2);
                  const event = parseSSEFrame(frame);
                  if (event) handleStreamEvent(event);
                  boundary = buffer.indexOf("\n\n");
                }
              }
            }
          } catch {
            // Network error, idle-watchdog abort or session abort — handled below.
          } finally {
            if (idleTimer) clearTimeout(idleTimer);
            session.signal.removeEventListener("abort", abortConnection);
          }

          if (!shouldReconnect || session.signal.aborted) return;
          const delay = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)];
          attempt += 1;
          reconnectTimer = setTimeout(connect, delay);
        };

        connect();
      });

    if (canCoordinate) {
      channel = new BroadcastChannel(coordinationKey);
      channel.onmessage = (messageEvent: MessageEvent<PackingAlertsChannelMessage>) => {
        const message = messageEvent.data;
        if (!message || session.signal.aborted) return;
        if (message.kind === "event") {
          // Same ts ordering + announcement grouping as the leader.
          applyEvent(message.event);
        } else if (message.kind === "sync") {
          // Catch-up state only — never replays an announcement.
          applyEvent({ ...message.event, type: "snapshot" });
        } else if (message.kind === "sync-request" && isLeader && lastAppliedTsRef.current > 0) {
          postToChannel({
            kind: "sync",
            event: {
              type: "snapshot",
              clinic_id: clinicId,
              sub_order_id: null,
              pending_count: pendingCountRef.current,
              ts: lastAppliedTsRef.current,
            },
          });
        }
      };
      // Ask the current leader (if any) for its latest count right away.
      postToChannel({ kind: "sync-request" });

      navigator.locks
        .request(coordinationKey, { signal: session.signal }, async () => {
          isLeader = true;
          try {
            await runStream();
          } finally {
            isLeader = false;
          }
        })
        .catch(() => {
          // AbortError: session ended while waiting for leadership.
        });
    } else {
      runStream();
    }

    return () => {
      session.abort();
      if (channel) {
        channel.onmessage = null;
        channel.close();
        channel = null;
      }
      countControllerRef.current?.abort();
      clearAnnouncement();
    };
  }, [isEnabled, clinicId, retryNonce, applyEvent, clearAnnouncement, getAuthorization, refreshCount]);

  // After a 401 stop, reconnect once the app has a refreshed access token.
  useEffect(() => {
    if (stoppedUnauthorizedRef.current && accessToken) {
      stoppedUnauthorizedRef.current = false;
      setRetryNonce((prev) => prev + 1);
    }
  }, [accessToken]);

  // Safety-net count refresh: tab visible again, or OMS Confirmed tab opened.
  useEffect(() => {
    if (!isEnabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshCount();
    };
    const handleRefreshRequest = () => {
      refreshCount();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener(PACKING_ALERTS_REFRESH_EVENT, handleRefreshRequest);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(PACKING_ALERTS_REFRESH_EVENT, handleRefreshRequest);
    };
  }, [isEnabled, refreshCount]);

  return {
    isEnabled,
    pendingCount: isEnabled ? pendingCount : 0,
    announcementCount: isEnabled ? announcementCount : 0,
    refreshCount,
    dismissAnnouncement: clearAnnouncement,
  };
};
